<!-- src/components/ResultDisplay.vue -->
<template>
  <div class="result-container">
    <!-- Carte principale du résultat -->
    <v-card class="main-card mb-6" rounded="xl" elevation="0">
      <!-- Entête avec le niveau atteint -->
      <v-card-item class="level-header pb-0">
        <div class="d-flex flex-column align-center text-center py-4">
          <v-avatar :color="result.levelColor" size="80" class="mb-4 elevation-4">
            <v-icon size="40" color="white">mdi-trophy</v-icon>
          </v-avatar>
          <h1 class="text-h3 font-weight-bold text-white mb-2">{{ result.levelName }}</h1>
          <div class="d-flex justify-center">
            <v-chip
              size="small"
              :color="gender === 'M' ? 'blue-darken-2' : 'pink-darken-2'"
              class="mr-2"
              variant="elevated"
            >
              {{ gender === "M" ? "Homme" : "Femme" }}
            </v-chip>
            <v-chip size="small" color="purple-darken-2" class="mr-2" variant="elevated">
              {{ ageCategoryDisplay }}
            </v-chip>
            <v-chip size="small" color="primary" variant="elevated">
              {{ result.weightCategory }}
            </v-chip>
          </div>
        </div>
      </v-card-item>

      <!-- Statistiques principales -->
      <v-card-text class="px-4 pt-6">
        <v-row>
          <!-- Carte hexagonale pour l'arraché -->
          <v-col cols="12" md="4">
            <div class="metric-card inflow">
              <div class="metric-title">
                <v-icon icon="mdi-arm-flex" class="mr-2"></v-icon>
                Arraché
              </div>
              <div class="metric-value">{{ snatch }} kg</div>
            </div>
          </v-col>

          <!-- Carte hexagonale pour l'épaulé-jeté -->
          <v-col cols="12" md="4">
            <div class="metric-card outflow">
              <div class="metric-title">
                <v-icon icon="mdi-weight-lifter" class="mr-2"></v-icon>
                Épaulé-jeté
              </div>
              <div class="metric-value">{{ cleanAndJerk }} kg</div>
            </div>
          </v-col>

          <!-- Carte hexagonale pour le total -->
          <v-col cols="12" md="4">
            <div class="metric-card net-change">
              <div class="metric-title">
                <v-icon icon="mdi-calculator" class="mr-2"></v-icon>
                Total
              </div>
              <div class="metric-value">{{ total }} kg</div>
            </div>
          </v-col>
        </v-row>

        <!-- Prochain objectif -->
        <v-card color="surface" class="mt-6 mb-4" elevation="0" rounded="lg">
          <v-card-title class="d-flex align-center">
            <v-icon icon="mdi-target" class="mr-2" color="primary"></v-icon>
            Prochain objectif
          </v-card-title>
          <v-card-text>
            <div v-if="result.nextLevel" class="d-flex flex-column align-center">
              <div class="text-h5 mb-3">{{ result.nextLevel.nom }}</div>
              <v-progress-linear
                :model-value="calculateProgressToNextLevel"
                height="20"
                :color="result.nextLevel.color"
                rounded
                class="mb-3"
              >
                <template v-slot:default>
                  <strong>{{ calculateProgressToNextLevel.toFixed(0) }}%</strong>
                </template>
              </v-progress-linear>
              <div class="text-body-1">
                <strong>{{ total }}</strong> / {{ result.nextLevel.seuil }} kg
                <span class="text-caption">(+{{ result.nextLevel.seuil - total }} kg pour atteindre ce niveau)</span>
              </div>
            </div>
            <div v-else class="text-center py-4">
              <v-icon color="success" size="40">mdi-check-circle</v-icon>
              <div class="text-h6 mt-2">Niveau maximum atteint !</div>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Cartes d'informations détaillées -->
    <v-row>
      <!-- Tableau des minima -->
      <v-col cols="12" lg="7">
        <v-card class="fill-height" rounded="xl" elevation="0">
          <v-card-title class="d-flex align-center">
            <v-icon icon="mdi-podium" class="mr-2" color="primary"></v-icon>
            Minima officiels
            <v-chip size="small" color="primary" class="ml-2" variant="tonal">
              {{ result.weightCategory }} - {{ ageCategoryDisplay }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <v-table class="minima-table">
              <thead>
                <tr>
                  <th>Niveau</th>
                  <th>Seuil</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="level in reversedLevels" :key="level.id">
                  <td>
                    <div class="d-flex align-center">
                      <v-avatar :color="level.color" size="24" class="mr-3">
                        <v-icon
                          size="16"
                          color="white"
                          :icon="
                            level.atteint
                              ? 'mdi-check'
                              : 'mdi-minus'
                          "
                        ></v-icon>
                      </v-avatar>
                      <span :class="{ 'font-weight-bold': level.id === result.levelId }">
                        {{ level.nom }}
                      </span>
                    </div>
                  </td>
                  <td>{{ level.seuil }} kg</td>
                  <td>
                    <v-chip
                      size="small"
                      :color="level.atteint ? 'success' : 'grey-darken-2'"
                      variant="tonal"
                    >
                      {{ level.atteint ? 'Atteint' : 'À venir' }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Progression graphique -->
      <v-col cols="12" lg="5">
        <v-card class="fill-height" rounded="xl" elevation="0">
          <v-card-title class="d-flex align-center">
            <v-icon icon="mdi-chart-bar" class="mr-2" color="primary"></v-icon>
            Progression
          </v-card-title>

          <v-card-text>
            <div v-if="result.allLevels && result.allLevels.length > 0">
              <div 
                v-for="level in sortedLevels" 
                :key="level.id" 
                class="progress-item mb-6"
              >
                <div class="d-flex justify-space-between align-center mb-1">
                  <span>{{ level.nom }}</span>
                  <span class="text-caption">{{ calculateProgressPercent(level).toFixed(0) }}%</span>
                </div>
                <v-progress-linear
                  :model-value="calculateProgressPercent(level)"
                  height="8"
                  :color="level.color"
                  rounded
                  class="progress-bar"
                ></v-progress-linear>
              </div>
            </div>
            <div v-else class="text-center py-8">
              <div class="text-body-1">Données de progression non disponibles</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- À propos des niveaux -->
    <v-expansion-panels class="mt-6" variant="accordion" rounded="xl">
      <v-expansion-panel elevation="0">
        <v-expansion-panel-title>
          <div class="d-flex align-center">
            <v-icon icon="mdi-information-outline" class="mr-2" color="info"></v-icon>
            À propos des niveaux d'haltérophilie
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p class="mb-3">
            Les niveaux sont basés sur les
            <strong>minima officiels</strong> de la Fédération Française
            d'Haltérophilie-Musculation pour la catégorie
            <strong>{{ result.ageCategory }}</strong>.
          </p>

          <v-row class="mt-4">
            <v-col cols="12" md="6" v-if="result.ageCategory === 'Master'">
              <v-list density="compact">
                <v-list-subheader>Catégories Masters</v-list-subheader>
                <v-list-item v-for="level in masterLevels" :key="level.id">
                  <template v-slot:prepend>
                    <v-avatar :color="level.color" size="24"></v-avatar>
                  </template>
                  <v-list-item-title>{{ level.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ level.description }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            
            <v-col cols="12" md="6" v-else>
              <v-list density="compact">
                <v-list-subheader>Catégories standard</v-list-subheader>
                <v-list-item v-for="level in standardLevels" :key="level.id">
                  <template v-slot:prepend>
                    <v-avatar :color="level.color" size="24"></v-avatar>
                  </template>
                  <v-list-item-title>{{ level.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ level.description }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  name: "ResultDisplay",
  props: {
    result: Object,
    age: Number,
    gender: String,
    bodyWeight: Number,
    snatch: Number,
    cleanAndJerk: Number,
    total: Number,
  },
  setup(props) {
    // Affichage de la catégorie d'âge
    const ageCategoryDisplay = computed(() => {
      const categoryMap = {
        U15: "Moins de 15 ans",
        U17: "Moins de 17 ans",
        U20: "Moins de 20 ans",
        Senior: "Senior",
        Master: "Master",
      };
      return categoryMap[props.result.ageCategory] || props.result.ageCategory;
    });

    // Niveaux triés du plus élevé au plus bas pour l'affichage
    const reversedLevels = computed(() => {
      if (!props.result.allLevels) return [];
      return [...props.result.allLevels].sort((a, b) => b.seuil - a.seuil);
    });

    // Niveaux triés du plus bas au plus élevé pour la progression
    const sortedLevels = computed(() => {
      if (!props.result.allLevels) return [];
      return [...props.result.allLevels]
        .filter((level) => level.seuil > 0)
        .sort((a, b) => b.seuil - a.seuil);
    });

    // Calcul du pourcentage de progression vers chaque niveau
    const calculateProgressPercent = (level) => {
      if (level.atteint) return 100;

      // Les niveaux ordonnés du plus bas au plus élevé par seuil
      const orderedLevels = [...props.result.allLevels]
        .filter(l => l.seuil > 0)
        .sort((a, b) => a.seuil - b.seuil);
      
      // Trouver la position du niveau actuel
      const levelIndex = orderedLevels.findIndex(l => l.id === level.id);
      
      // S'il s'agit du niveau le plus bas ou si on ne trouve pas le niveau
      if (levelIndex <= 0) {
        // Calculer le pourcentage par rapport au niveau de base (0)
        return Math.min(99, (props.total / level.seuil) * 100);
      }
      
      // Trouver le niveau précédent
      const previousLevel = orderedLevels[levelIndex - 1];
      
      // Calculer la progression entre le niveau précédent et le niveau actuel
      const totalProgress = level.seuil - previousLevel.seuil;
      const currentProgress = props.total - previousLevel.seuil;
      
      // Calculer le pourcentage (limité à 99% s'il n'est pas atteint)
      const percent = (currentProgress / totalProgress) * 100;
      return Math.max(0, Math.min(99, percent));
    };

    // Calcul spécifique de la progression vers le prochain niveau
    const calculateProgressToNextLevel = computed(() => {
      if (!props.result.nextLevel) return 100;
      
      // Trouver le niveau actuel
      const currentLevel = props.result.allLevels.find(
        (l) => l.id === props.result.levelId
      );
      
      if (!currentLevel) return 0;
      
      // Calculer la progression
      const currentThreshold = currentLevel.seuil;
      const nextThreshold = props.result.nextLevel.seuil;
      const progress = props.total - currentThreshold;
      const totalNeeded = nextThreshold - currentThreshold;
      
      return Math.min(99, (progress / totalNeeded) * 100);
    });

    // Informations sur les niveaux masters
    const masterLevels = [
      { id: "monde", name: "Niveau Monde", description: "Minima pour les championnats du monde Masters", color: "deep-orange-accent-3" },
      { id: "europe", name: "Niveau Europe", description: "Minima pour les championnats d'Europe Masters", color: "amber-darken-2" },
      { id: "france", name: "Niveau France", description: "Minima pour les championnats de France Masters", color: "deep-purple-darken-1" },
      { id: "region", name: "Niveau Région", description: "Minima pour les championnats régionaux Masters", color: "blue-darken-1" }
    ];

    // Informations sur les niveaux standard
    const standardLevels = [
      { id: "intA", name: "International A", description: "Niveau international élite", color: "deep-orange-accent-3" },
      { id: "intB", name: "International B", description: "Niveau requis pour les compétitions internationales", color: "amber-darken-2" },
      { id: "nat", name: "National", description: "Niveau requis pour les championnats de France", color: "deep-purple-darken-1" },
      { id: "fed", name: "Fédéral", description: "Niveau pour les compétitions fédérales", color: "indigo-darken-1" },
      { id: "irg", name: "Interrégional", description: "Niveau pour les compétitions interrégionales", color: "blue-darken-1" },
      { id: "reg", name: "Régional", description: "Niveau pour les compétitions régionales", color: "teal-darken-1" },
      { id: "dpt", name: "Départemental", description: "Niveau pour les compétitions départementales", color: "green-darken-1" }
    ];

    return {
      ageCategoryDisplay,
      reversedLevels,
      sortedLevels,
      calculateProgressPercent,
      calculateProgressToNextLevel,
      masterLevels,
      standardLevels
    };
  }
};
</script>

<style scoped>
.result-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.main-card {
  background: linear-gradient(to right bottom, #232526, #414345);
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.level-header {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), transparent);
  padding-top: 2rem;
}

.metric-card {
  background-color: #1e1e1e;
  border-radius: 16px;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
}

.metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.metric-card.inflow {
  border-top: 3px solid #73e067;
}

.metric-card.outflow {
  border-top: 3px solid #9f94ff;
}

.metric-card.net-change {
  border-top: 3px solid #ff9773;
}

.metric-title {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
}

.metric-value {
  font-size: 2.5rem;
  font-weight: bold;
  line-height: 1.2;
}

.minima-table {
  background: transparent !important;
}

.minima-table tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.minima-table th {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  font-size: 0.875rem;
}

.progress-item {
  position: relative;
}

.progress-bar {
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
}

.v-card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.v-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

@media (max-width: 600px) {
  .metric-value {
    font-size: 1.75rem;
  }
  
  .level-header {
    padding-top: 1rem;
  }
}
/* Corrections pour mobile */
@media (max-width: 600px) {
  .metric-card {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .metric-value {
    font-size: 1.8rem; /* Taille de police réduite sur mobile */
  }
  
  .main-card {
    padding: 1rem;
  }
  
  .level-header {
    padding-top: 1rem;
    padding-bottom: 0.5rem;
  }
  
  .total-hexagon {
    width: 150px; /* Hexagone plus petit sur mobile */
    height: 150px;
  }
}
</style>